import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Mail, Lock, User, Chrome } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, formData);
      toast.success('Account created successfully!');
      
      // Auto-login after registration
      const loginResponse = await axios.post(
        `${API}/auth/login`,
        { email: formData.email, password: formData.password },
        { withCredentials: true }
      );
      navigate('/dashboard', { state: { user: loginResponse.data.user } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12" data-testid="signup-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/s2b-hub-logo.png" alt="The Hub by S2B Group" className="h-16 w-16 object-contain" />
            <div className="flex flex-col leading-none"><span className="text-3xl font-outfit font-bold text-primary">The Hub</span><span className="text-sm text-slate-600 font-normal">by S2B Group</span></div>
          </div>
          <h1 className="text-3xl font-outfit font-bold text-primary mb-2">Create Account</h1>
          <p className="text-slate-600">Join South Africa's business community</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form">
            <div>
              <Label htmlFor="name" className="text-primary font-medium">Full Name</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 focus:border-accent-cyan focus:ring-accent-cyan/20 rounded-lg h-12"
                  placeholder="John Smith"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-primary font-medium">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 focus:border-accent-cyan focus:ring-accent-cyan/20 rounded-lg h-12"
                  placeholder="your@email.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-primary font-medium">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 bg-slate-50 border-slate-200 focus:border-accent-cyan focus:ring-accent-cyan/20 rounded-lg h-12"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-accent-orange text-white hover:bg-[#D97000] rounded-full py-6 text-lg font-medium hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account...' : 'Sign up'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleGoogleSignup}
              variant="outline"
              className="w-full mt-6 border-slate-200 hover:bg-slate-50 rounded-full py-6 text-lg font-medium"
              data-testid="google-signup-button"
            >
              <Chrome className="mr-2 h-5 w-5" />
              Google
            </Button>
          </div>

          <p className="mt-8 text-center text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-accent-cyan hover:underline font-medium" data-testid="login-link">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
